// Intelligence Dashboard views (additive). Raw-Tailwind dark theme to match the
// existing app. Founder-gated data views + Veo video-editor workflow.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
// `user` is passed in as a prop from App.js (which owns the auth context) to
// avoid a circular import with App.js.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AX = { withCredentials: true };

// ── shared formatting + chips ────────────────────────────────────────────────
const pct = (v) => (v == null ? "-" : `${(Number(v) * 100).toFixed(1)}%`);
const pctRaw = (v) => (v == null ? "-" : `${Number(v).toFixed(2)}%`);
const money = (v) => (v == null ? "-" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const money0 = (v) => (v == null ? "-" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const excerpt = (s, n = 120) => (!s ? "" : s.length > n ? s.slice(0, n) + "…" : s);

const TONE = {
  slate: "bg-slate-700/40 text-slate-300 border-slate-600/40",
  blue: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  violet: "bg-violet-500/15 text-violet-300 border-violet-500/30",
};
const STATE_TONE = { COLLECTING: "slate", EARLY_SIGNAL: "blue", MEDIUM_CONFIDENCE: "amber", HIGH_CONFIDENCE: "emerald", INVALIDATED: "rose", INCONCLUSIVE: "slate" };
const ACTION_TONE = { SCALE: "emerald", KILL: "rose", ITERATE: "amber", HOLD: "blue", INSUFFICIENT_DATA: "slate" };
const TIER_TONE = { LOW: "slate", MEDIUM: "amber", HIGH: "emerald" };
const PRIORITY_TONE = { HIGH: "rose", MEDIUM: "amber", LOW: "slate" };
const STATUS_TONE = { PROPOSED: "blue", ACCEPTED: "emerald", IN_TEST: "amber", VALIDATED: "emerald", INVALIDATED: "rose", ABANDONED: "slate", PENDING: "slate", DRAFT_GENERATED: "blue", IN_REVIEW: "amber", APPROVED: "emerald", SUBMITTED: "violet", COMPLETED: "emerald" };

const Chip = ({ children, tone = "slate" }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${TONE[tone] || TONE.slate}`}>{children}</span>
);

const Spinner = () => (
  <div className="flex items-center justify-center h-64" data-testid="loading">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
  </div>
);
const Empty = ({ label }) => (
  <div className="text-center py-16 text-slate-500" data-testid="empty">{label}</div>
);
const ErrorBanner = ({ msg }) => (msg ? <div className="mb-4 px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm">{msg}</div> : null);

const PageHead = ({ title, subtitle, right }) => (
  <div className="flex items-start justify-between mb-6">
    <div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
      {subtitle && <p className="text-slate-400 mt-1 text-sm">{subtitle}</p>}
    </div>
    {right}
  </div>
);

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs text-slate-400">
    <span className="uppercase tracking-wide">{label}</span>
    {children}
  </label>
);
const selectCls = "bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/50";
const uniq = (arr) => Array.from(new Set(arr.filter((x) => x != null && x !== "")));

// Small overlay modal
const Modal = ({ open, onClose, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className={`relative bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full ${wide ? "max-w-5xl" : "max-w-2xl"} max-h-[85vh] overflow-auto`}>
        {children}
      </div>
    </div>
  );
};

// ═════════════════════════════ EXPERIMENTS ═══════════════════════════════════
export function ExperimentsPage() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [fState, setFState] = useState("ALL");
  const [fAction, setFAction] = useState("ALL");
  const [fBU, setFBU] = useState("ALL");
  const [sortBy, setSortBy] = useState("current_spend");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/intelligence/experiments`, AX);
        if (alive) setRows(res.data.experiments || []);
      } catch (e) {
        if (alive) { setErr("Failed to load experiments."); setRows([]); }
      }
    })();
    return () => { alive = false; };
  }, []);

  if (rows === null) return <Spinner />;

  let view = rows.filter((r) =>
    (fState === "ALL" || r.experiment_state === fState) &&
    (fAction === "ALL" || r.recommended_action === fAction) &&
    (fBU === "ALL" || r.business_unit === fBU));
  view = [...view].sort((a, b) => (Number(b[sortBy] ?? -Infinity) - Number(a[sortBy] ?? -Infinity)));

  return (
    <div>
      <PageHead title="Experiments" subtitle={`${rows.length} experiments`} />
      <ErrorBanner msg={err} />
      <div className="flex flex-wrap gap-4 mb-4">
        <Field label="State"><select className={selectCls} value={fState} onChange={(e) => setFState(e.target.value)}>
          <option value="ALL">All states</option>{uniq(rows.map((r) => r.experiment_state)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Action"><select className={selectCls} value={fAction} onChange={(e) => setFAction(e.target.value)}>
          <option value="ALL">All actions</option>{uniq(rows.map((r) => r.recommended_action)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Business Unit"><select className={selectCls} value={fBU} onChange={(e) => setFBU(e.target.value)}>
          <option value="ALL">All BUs</option>{uniq(rows.map((r) => r.business_unit)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Sort by"><select className={selectCls} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="current_spend">Current Spend</option>
          <option value="result_ctr">Result CTR</option>
          <option value="result_profit">Result Profit</option></select></Field>
      </div>

      {view.length === 0 ? <Empty label="No experiments match these filters." /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                {["Ad Name", "Channel", "State", "Current Spend", "Result CTR", "Result Profit", "Recommended Action"].map((h) => (
                  <th key={h} className="text-left font-medium px-4 py-3">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {view.map((r) => (
                <tr key={r.experiment_id} onClick={() => setDetail(r)}
                    className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" data-testid="exp-row">
                  <td className="px-4 py-3 text-slate-200 font-mono text-xs">{r.creative_id || r.ad_id || "-"}</td>
                  <td className="px-4 py-3 text-slate-300">{r.channel || "-"}</td>
                  <td className="px-4 py-3"><Chip tone={STATE_TONE[r.experiment_state]}>{r.experiment_state}</Chip></td>
                  <td className="px-4 py-3 text-slate-200">{money(r.current_spend)}</td>
                  <td className="px-4 py-3 text-slate-200">{pctRaw(r.result_ctr)}</td>
                  <td className="px-4 py-3 text-slate-200">{r.result_profit == null ? "-" : money(r.result_profit)}</td>
                  <td className="px-4 py-3">{r.recommended_action ? <Chip tone={ACTION_TONE[r.recommended_action]}>{r.recommended_action}</Chip> : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!detail} onClose={() => setDetail(null)} wide>
        {detail && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Experiment detail</h3>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white text-sm">Close</button>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {Object.entries(detail).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3 border-b border-slate-800/60 py-1">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-200 text-right break-all">{v == null ? "-" : (typeof v === "object" ? JSON.stringify(v) : String(v))}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ═════════════════════════════ OBSERVATIONS ══════════════════════════════════
export function ObservationsPage() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [fDim, setFDim] = useState("ALL");
  const [fTier, setFTier] = useState("ALL");
  const [fBU, setFBU] = useState("ALL");
  const [sortBy, setSortBy] = useState("effect_size");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/intelligence/observations`, AX);
        if (alive) setRows(res.data.observations || []);
      } catch (e) { if (alive) { setErr("Failed to load observations."); setRows([]); } }
    })();
    return () => { alive = false; };
  }, []);

  if (rows === null) return <Spinner />;
  const tierRank = { HIGH: 3, MEDIUM: 2, LOW: 1 };
  let view = rows.filter((r) =>
    (fDim === "ALL" || r.dimension === fDim) &&
    (fTier === "ALL" || r.confidence_tier === fTier) &&
    (fBU === "ALL" || r.business_unit === fBU));
  view = [...view].sort((a, b) => {
    if (sortBy === "confidence_tier") return (tierRank[b.confidence_tier] || 0) - (tierRank[a.confidence_tier] || 0);
    if (sortBy === "created_at") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    return Number(b.effect_size ?? -Infinity) - Number(a.effect_size ?? -Infinity);
  });

  return (
    <div>
      <PageHead title="Observations" subtitle={`${rows.length} observations`} />
      <ErrorBanner msg={err} />
      <div className="flex flex-wrap gap-4 mb-5">
        <Field label="Dimension"><select className={selectCls} value={fDim} onChange={(e) => setFDim(e.target.value)}>
          <option value="ALL">All dimensions</option>{uniq(rows.map((r) => r.dimension)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Confidence"><select className={selectCls} value={fTier} onChange={(e) => setFTier(e.target.value)}>
          <option value="ALL">All tiers</option>{["HIGH", "MEDIUM", "LOW"].map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Business Unit"><select className={selectCls} value={fBU} onChange={(e) => setFBU(e.target.value)}>
          <option value="ALL">All BUs</option>{uniq(rows.map((r) => r.business_unit)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Sort by"><select className={selectCls} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="effect_size">Effect size</option>
          <option value="confidence_tier">Confidence</option>
          <option value="created_at">Created</option></select></Field>
      </div>

      {view.length === 0 ? <Empty label="No observations match these filters." /> : (
        <div className="grid gap-4 md:grid-cols-2">
          {view.map((r) => (
            <div key={r.observation_id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="obs-card">
              <p className="text-slate-100 text-base leading-relaxed mb-4">{r.observation_text}</p>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Chip tone="violet">{r.dimension}</Chip>
                <Chip tone={TIER_TONE[r.confidence_tier]}>{r.confidence_tier} confidence</Chip>
                <Chip tone={(r.effect_size || 0) >= 0 ? "emerald" : "rose"}>{pct(r.effect_size)}</Chip>
                <Chip tone="slate">{r.primary_metric}</Chip>
              </div>
              <div className="flex gap-6 text-xs text-slate-400">
                <span>{r.experiment_count} experiments</span>
                <span>{money0(r.total_spend_analyzed)} analyzed</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════ HYPOTHESES ════════════════════════════════════
export function HypothesesPage({ user }) {
  const isFounder = user?.role === "FOUNDER";
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({});
  const [brief, setBrief] = useState(null);
  const [fStatus, setFStatus] = useState("ALL");
  const [fPriority, setFPriority] = useState("ALL");
  const [fBU, setFBU] = useState("ALL");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/intelligence/hypotheses`, AX);
      setRows(res.data.hypotheses || []);
    } catch (e) { setErr("Failed to load hypotheses."); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, kind) => {
    setBusy(id + kind); setErr("");
    try {
      await axios.post(`${API}/intelligence/hypotheses/${id}/${kind}`, {}, AX);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || `Action ${kind} failed.`);
    } finally { setBusy(""); }
  };

  if (rows === null) return <Spinner />;
  const view = rows.filter((r) =>
    (fStatus === "ALL" || r.status === fStatus) &&
    (fPriority === "ALL" || r.priority === fPriority) &&
    (fBU === "ALL" || r.business_unit === fBU));

  return (
    <div>
      <PageHead title="Hypotheses" subtitle={`${rows.length} hypotheses`} />
      <ErrorBanner msg={err} />
      <div className="flex flex-wrap gap-4 mb-5">
        <Field label="Status"><select className={selectCls} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="ALL">All statuses</option>{uniq(rows.map((r) => r.status)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Priority"><select className={selectCls} value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
          <option value="ALL">All priorities</option>{["HIGH", "MEDIUM", "LOW"].map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Business Unit"><select className={selectCls} value={fBU} onChange={(e) => setFBU(e.target.value)}>
          <option value="ALL">All BUs</option>{uniq(rows.map((r) => r.business_unit)).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
      </div>

      {view.length === 0 ? <Empty label="No hypotheses match these filters." /> : (
        <div className="grid gap-4">
          {view.map((r) => (
            <div key={r.hypothesis_id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="hyp-card">
              <div className="flex items-start justify-between gap-3 mb-3">
                <p className="text-slate-100 text-base leading-relaxed">{r.hypothesis_text}</p>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Chip tone={PRIORITY_TONE[r.priority]}>{r.priority}</Chip>
                  <Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <Chip tone="violet">var: {r.independent_variable}</Chip>
                <Chip tone="emerald">test: {excerpt(r.proposed_value, 40)}</Chip>
                <Chip tone="slate">vs {excerpt(r.control_value, 40)}</Chip>
                <Chip tone="blue">conf {pct(r.confidence_in_hypothesis)}</Chip>
              </div>
              <button onClick={() => setExpanded((p) => ({ ...p, [r.hypothesis_id]: !p[r.hypothesis_id] }))}
                      className="text-xs text-slate-400 hover:text-slate-200 mb-2">
                {expanded[r.hypothesis_id] ? "Hide rationale ▲" : "Show rationale ▼"}
              </button>
              {expanded[r.hypothesis_id] && <p className="text-slate-400 text-sm leading-relaxed mb-3 border-l-2 border-slate-700 pl-3">{r.rationale}</p>}

              {isFounder && (
                <div className="flex gap-2 pt-2 border-t border-slate-800">
                  {r.status === "PROPOSED" && (
                    <button disabled={busy === r.hypothesis_id + "accept"} onClick={() => act(r.hypothesis_id, "accept")}
                            className="px-3 py-1.5 rounded-lg text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-50" data-testid="hyp-accept">
                      {busy === r.hypothesis_id + "accept" ? "Accepting…" : "Accept"}</button>)}
                  {r.status === "ACCEPTED" && (
                    <button disabled={busy === r.hypothesis_id + "brief"} onClick={() => act(r.hypothesis_id, "brief")}
                            className="px-3 py-1.5 rounded-lg text-sm bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 disabled:opacity-50" data-testid="hyp-brief">
                      {busy === r.hypothesis_id + "brief" ? "Generating…" : "Generate Brief"}</button>)}
                  {r.status === "IN_TEST" && r.creative_brief && (
                    <button onClick={() => setBrief(r)}
                            className="px-3 py-1.5 rounded-lg text-sm bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60" data-testid="hyp-viewbrief">
                      View Brief</button>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={!!brief} onClose={() => setBrief(null)} wide>
        {brief && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Creative Brief</h3>
              <button onClick={() => setBrief(null)} className="text-slate-400 hover:text-white text-sm">Close</button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-slate-200 leading-relaxed font-sans">{brief.creative_brief}</pre>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ═════════════════════════════ VEO QUEUE ═════════════════════════════════════
const VEO_STEPS = ["PENDING", "DRAFT_GENERATED", "IN_REVIEW", "APPROVED", "SUBMITTED", "COMPLETED"];

const Stepper = ({ status }) => {
  const idx = VEO_STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {VEO_STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${i <= idx ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-500"}`}>{s}</span>
          {i < VEO_STEPS.length - 1 && <span className={`text-xs ${i < idx ? "text-emerald-500" : "text-slate-700"}`}>→</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

export function VeoQueuePage({ user }) {
  const allowed = ["VIDEO_EDITOR", "FOUNDER"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [review, setReview] = useState(null);  // queue item being edited
  const [scoreFor, setScoreFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/veo/queue`, AX);
      setRows(res.data.queue || []);
    } catch (e) { setErr("Failed to load Veo queue."); setRows([]); }
  }, []);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) return <div className="text-center py-16 text-slate-500">Video editor access required.</div>;
  if (rows === null) return <Spinner />;

  const post = async (id, kind) => {
    setBusy(id + kind); setErr("");
    try { await axios.post(`${API}/veo/queue/${id}/${kind}`, {}, AX); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || `${kind} failed.`); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <PageHead title="Veo Queue" subtitle={`${rows.length} briefs in the video-editor queue`} />
      <ErrorBanner msg={err} />
      {rows.length === 0 ? <Empty label="No briefs in the queue yet." /> : (
        <div className="grid gap-4">
          {rows.map((it) => (
            <div key={it.queue_id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-5" data-testid="veo-item">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-slate-100">{excerpt(it.creative_brief || it.hypothesis_id, 140)}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-400">
                    <Chip tone="violet">{it.production_style}</Chip>
                    <Chip tone={STATUS_TONE[it.status]}>{it.status}</Chip>
                    <span>assigned: {it.assigned_to || "—"}</span>
                    <span>{it.created_at ? String(it.created_at).slice(0, 10) : ""}</span>
                  </div>
                </div>
              </div>
              <div className="mb-3"><Stepper status={it.status} /></div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800">
                {it.status === "PENDING" && (
                  <button disabled={busy === it.queue_id + "generate-draft"} onClick={() => post(it.queue_id, "generate-draft")}
                          className="px-3 py-1.5 rounded-lg text-sm bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 disabled:opacity-50" data-testid="veo-draft">
                    {busy === it.queue_id + "generate-draft" ? "Generating…" : "Generate Draft"}</button>)}
                {it.status === "DRAFT_GENERATED" && (
                  <button onClick={() => setReview(it)}
                          className="px-3 py-1.5 rounded-lg text-sm bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25" data-testid="veo-review">
                    Review &amp; Edit</button>)}
                {["IN_REVIEW", "APPROVED"].includes(it.status) && (
                  <button disabled={busy === it.queue_id + "submit"} onClick={() => post(it.queue_id, "submit")}
                          className="px-3 py-1.5 rounded-lg text-sm bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 disabled:opacity-50" data-testid="veo-submit">
                    {busy === it.queue_id + "submit" ? "Submitting…" : "Submit to Veo"}</button>)}
                {it.status === "SUBMITTED" && (
                  <button onClick={() => setScoreFor(it)}
                          className="px-3 py-1.5 rounded-lg text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25" data-testid="veo-scorebtn">
                    Score</button>)}
              </div>
            </div>
          ))}
        </div>
      )}

      <ReviewModal item={review} onClose={() => setReview(null)} onSaved={async () => { setReview(null); await load(); }} setErr={setErr} />
      <ScoreModal item={scoreFor} user={user} onClose={() => setScoreFor(null)} onScored={async () => { setScoreFor(null); await load(); }} setErr={setErr} />
    </div>
  );
}

const SECTIONS = [
  ["scene_and_subject", "Scene & Subject"],
  ["mood_pacing_style", "Mood, Pacing & Style"],
  ["opening_hook_direction", "Opening Hook Direction"],
  ["technical_specs", "Technical Specs"],
];

function ReviewModal({ item, onClose, onSaved, setErr }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!item) { setForm(null); return; }
    const d = item.veo_prompt_draft || {};
    setForm({
      scene_and_subject: d.scene_and_subject || "",
      mood_pacing_style: d.mood_pacing_style || "",
      opening_hook_direction: d.opening_hook_direction || "",
      technical_specs: d.technical_specs || "",
      full_prompt: item.full_prompt_draft || "",
    });
  }, [item]);
  if (!item || !form) return null;

  const save = async () => {
    setSaving(true); setErr("");
    try { await axios.patch(`${API}/veo/queue/${item.queue_id}/prompt`, form, AX); await onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };
  const taCls = "bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 w-full focus:outline-none focus:border-emerald-500/50";

  return (
    <Modal open={!!item} onClose={onClose} wide>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Review &amp; Edit Prompt</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">Close</button>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Creative Brief (read-only)</p>
            <pre className="whitespace-pre-wrap text-xs text-slate-300 leading-relaxed font-sans bg-slate-950/50 border border-slate-800 rounded-lg p-3 max-h-[60vh] overflow-auto">{item.creative_brief || "—"}</pre>
          </div>
          <div className="space-y-3">
            {SECTIONS.map(([k, label]) => (
              <div key={k}>
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</p>
                <textarea className={taCls} rows={3} value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} data-testid={`veo-sec-${k}`} />
              </div>
            ))}
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Full Prompt (Veo)</p>
              <textarea className={taCls} rows={4} value={form.full_prompt} onChange={(e) => setForm((p) => ({ ...p, full_prompt: e.target.value }))} data-testid="veo-fullprompt" />
            </div>
            <button disabled={saving} onClick={save}
                    className="px-4 py-2 rounded-lg text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-50" data-testid="veo-save">
              {saving ? "Saving…" : "Save Prompt"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ScoreModal({ item, user, onClose, onScored, setErr }) {
  const [v, setV] = useState({ visual_match: 3, tone_match: 3, hook_strength: 3, visual_match_note: "", tone_match_note: "", hook_strength_note: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setV({ visual_match: 3, tone_match: 3, hook_strength: 3, visual_match_note: "", tone_match_note: "", hook_strength_note: "" }); }, [item]);
  if (!item) return null;

  const dims = [["visual_match", "Visual Match"], ["tone_match", "Tone Match"], ["hook_strength", "Hook Strength"]];
  const submit = async () => {
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/veo/queue/${item.queue_id}/score`, { ...v, scored_by: user?.operator_id || user?.operator_code || "" }, AX);
      await onScored();
    } catch (e) { setErr(e?.response?.data?.detail || "Score failed."); }
    finally { setSaving(false); }
  };
  const taCls = "bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 w-full focus:outline-none focus:border-emerald-500/50";

  return (
    <Modal open={!!item} onClose={onClose}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Scorecard</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">Close</button>
        </div>
        <div className="space-y-5">
          {dims.map(([k, label]) => (
            <div key={k}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-200">{label}</span>
                <span className="text-sm font-semibold text-emerald-300">{v[k]}/5</span>
              </div>
              <input type="range" min={1} max={5} step={1} value={v[k]} onChange={(e) => setV((p) => ({ ...p, [k]: Number(e.target.value) }))}
                     className="w-full accent-emerald-500" data-testid={`score-${k}`} />
              <textarea className={taCls + " mt-2"} rows={2} placeholder={`${label} note`} value={v[k + "_note"]}
                        onChange={(e) => setV((p) => ({ ...p, [k + "_note"]: e.target.value }))} />
            </div>
          ))}
          <button disabled={saving} onClick={submit}
                  className="w-full px-4 py-2 rounded-lg text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-50" data-testid="score-submit">
            {saving ? "Submitting…" : "Submit Score"}</button>
        </div>
      </div>
    </Modal>
  );
}
